<template>
  <header ref="headerRoot" class="fusion-header-wrapper">
    <div
      class="fusion-header-v1 fusion-logo-left fusion-sticky-menu- fusion-mobile-menu-design-modern"
    >
      <div class="fusion-header">
        <div class="fusion-row">
          <div class="fusion-logo">
            <router-link class="fusion-logo-link" to="/">
              <img src="/logo.png" alt="Boris Bags" class="fusion-standard-logo" />
            </router-link>
          </div>

          <nav class="fusion-main-menu" :aria-label="$t('nav.home')">
            <ul id="menu-one-menu" class="fusion-menu">
              <li
                v-for="item in mainNavItems"
                :key="item.id"
                class="menu-item"
                :class="[
                  item.class,
                  {
                    'current-menu-item': isNavActive(item) && !item.button,
                    'is-megamenu-open': item.megamenu && megamenuOpen
                  }
                ]"
                @mouseenter="onNavItemMouseEnter(item)"
              >
                <router-link
                  v-if="!item.megamenu"
                  :to="item.to"
                  class="fusion-bar-highlight"
                  role="menuitem"
                  active-class=""
                >
                  <span
                    class="menu-text"
                    :class="{
                      'fusion-button button-default button-medium fusion-button-round': item.button
                    }"
                  >
                    {{ item.label }}
                  </span>
                </router-link>

                <template v-else>
                  <div
                    class="fusion-megamenu-anchor"
                    @mouseenter="openMegamenu"
                    @mouseleave="scheduleCloseMegamenu"
                  >
                    <router-link
                      to="/products"
                      class="fusion-bar-highlight"
                      role="menuitem"
                      aria-haspopup="true"
                      :aria-expanded="megamenuOpen ? 'true' : 'false'"
                    >
                      <span class="menu-text">{{ item.label }}</span>
                    </router-link>
                  </div>
                  <div
                    class="product-category-panel"
                    :class="{ 'is-open': megamenuOpen }"
                    @mouseenter="openMegamenu"
                    @mouseleave="scheduleCloseMegamenu"
                  >
                    <div class="product-category-panel__inner">
                      <router-link
                        v-for="cat in megamenuCategories"
                        :key="cat.slug || cat.id"
                        :to="categoryProductLink(cat.slug)"
                        class="product-category-panel__link"
                        @click="onCategorySelect"
                      >
                        <img
                          v-if="categoryImageUrl(cat)"
                          :src="categoryImageUrl(cat)"
                          :alt="$categoryName(cat)"
                          class="product-category-panel__img"
                        />
                        <span class="product-category-panel__label">{{ $categoryName(cat) }}</span>
                      </router-link>
                    </div>
                  </div>
                </template>
              </li>

              <li class="fusion-custom-menu-item fusion-locale-switcher-item">
                <LocaleSwitcher class="locale-switcher--header" />
              </li>

              <li class="fusion-custom-menu-item fusion-main-menu-search fusion-last-menu-item">
                <a
                  class="fusion-main-menu-icon fusion-bar-highlight"
                  href="#"
                  :aria-label="$t('nav.search')"
                  :title="$t('nav.search')"
                  @click.prevent
                />
              </li>
            </ul>
          </nav>

          <div class="fusion-mobile-menu-icons">
            <LocaleSwitcher class="locale-switcher--mobile" />
            <a
              href="#"
              class="fusion-icon fusion-icon-bars"
              aria-label="Toggle mobile menu"
              :aria-expanded="mobileOpen ? 'true' : 'false'"
              aria-controls="mobile-menu-one-menu"
              @click.prevent="toggleMobile"
            />
          </div>

          <nav
            class="fusion-mobile-nav-holder fusion-mobile-menu-text-align-left"
            aria-label="Main Menu Mobile"
            :style="{ display: mobileOpen ? 'block' : 'none' }"
          >
            <ul id="mobile-menu-one-menu" class="fusion-menu">
              <li
                v-for="item in mainNavItems"
                :key="'m-' + item.id"
                class="fusion-mobile-nav-item"
                :class="{
                  'fusion-mobile-current-nav-item': isNavActive(item) && !item.button,
                  'menu-item-has-children': item.megamenu,
                  'fusion-menu-item-button': item.button
                }"
              >
                <router-link
                  :to="item.to"
                  class="fusion-bar-highlight"
                  role="menuitem"
                  @click="onMobileNavClick(item)"
                >
                  <span
                    class="menu-text"
                    :class="{
                      'fusion-button button-default button-medium fusion-button-round': item.button
                    }"
                  >
                    {{ item.label }}
                  </span>
                </router-link>
                <button
                  v-if="item.megamenu"
                  type="button"
                  class="fusion-open-submenu"
                  :aria-expanded="mobileProductsOpen ? 'true' : 'false'"
                  aria-label="Open Sub Menu"
                  @click.prevent="mobileProductsOpen = !mobileProductsOpen"
                />
                <ul
                  v-if="item.megamenu && mobileProductsOpen"
                  role="menu"
                  class="sub-menu"
                >
                  <li
                    v-for="cat in megamenuCategories"
                    :key="'m-cat-' + cat.slug"
                    class="fusion-mobile-nav-item"
                  >
                    <router-link :to="categoryProductLink(cat.slug)" @click="closeMobile">
                      {{ $categoryName(cat) }}
                    </router-link>
                  </li>
                </ul>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>
    <div class="fusion-clearfix" />
  </header>
</template>

<script>
import {
  categoryProductLink,
  fetchPublicCategories,
  mapStorefrontCategories,
  resolveUploadUrl
} from '@/api/categories'
import LocaleSwitcher from '@/components/LocaleSwitcher.vue'
import { useStorefrontTheme } from '@/composables/useStorefrontTheme'

const NAV_ITEMS = [
  { id: 'home', labelKey: 'nav.home', to: '/', match: (path) => path === '/' },
  {
    id: 'products',
    labelKey: 'nav.products',
    to: '/products',
    megamenu: true,
    class: 'fusion-megamenu-menu menu-item-has-children',
    match: (path) => path.startsWith('/products')
  },
  {
    id: 'about',
    labelKey: 'nav.about',
    to: '/about',
    match: (path) => path.startsWith('/about')
  },
  {
    id: 'news',
    labelKey: 'nav.news',
    to: '/news',
    match: (path) => path.startsWith('/news')
  },
  {
    id: 'contact',
    labelKey: 'nav.contact',
    to: '/contact',
    match: (path) => path.startsWith('/contact')
  },
  {
    id: 'quote',
    labelKey: 'nav.quote',
    to: '/contact',
    button: true,
    class: 'fusion-menu-item-button',
    match: () => false
  }
]

export default {
  name: 'SiteHeader',
  components: { LocaleSwitcher },
  setup() {
    useStorefrontTheme()
  },
  data() {
    return {
      megamenuCategories: [],
      megamenuOpen: false,
      megamenuCloseTimer: null,
      mobileOpen: false,
      mobileProductsOpen: false
    }
  },
  computed: {
    mainNavItems() {
      void this.currentLocale
      return NAV_ITEMS.map((item) => ({
        ...item,
        label: this.$t(item.labelKey)
      }))
    }
  },
  watch: {
    $route() {
      this.closeMegamenu()
      this.closeMobile()
    }
  },
  mounted() {
    this.loadMegamenuCategories()
    this.$nextTick(() => this.initMobileSubmenus())
  },
  beforeUnmount() {
    clearTimeout(this.megamenuCloseTimer)
  },
  methods: {
    isNavActive(item) {
      return item.match(this.$route.path)
    },
    categoryProductLink,
    categoryImageUrl(cat) {
      if (!cat) return ''
      return resolveUploadUrl(cat.image_url || cat.image_path || '')
    },
    async loadMegamenuCategories() {
      try {
        const res = await fetchPublicCategories()
        this.megamenuCategories = mapStorefrontCategories(res.data?.items || [])
      } catch {
        this.megamenuCategories = []
      }
    },
    openMegamenu() {
      clearTimeout(this.megamenuCloseTimer)
      this.megamenuOpen = true
    },
    scheduleCloseMegamenu() {
      clearTimeout(this.megamenuCloseTimer)
      this.megamenuCloseTimer = setTimeout(() => {
        this.megamenuOpen = false
      }, 280)
    },
    onNavItemMouseEnter(item) {
      if (!item.megamenu) {
        this.closeMegamenu()
      }
    },
    closeMegamenu() {
      clearTimeout(this.megamenuCloseTimer)
      this.megamenuOpen = false
    },
    onCategorySelect() {
      this.closeMegamenu()
    },
    toggleMobile() {
      this.mobileOpen = !this.mobileOpen
      if (!this.mobileOpen) {
        this.mobileProductsOpen = false
      }
    },
    closeMobile() {
      this.mobileOpen = false
      this.mobileProductsOpen = false
    },
    onMobileNavClick(item) {
      if (!item.megamenu) {
        this.closeMobile()
      }
    },
    initMobileSubmenus() {
      /* 子菜单由 Vue 控制，无需额外 DOM 绑定 */
    }
  }
}
</script>

<style scoped>
.fusion-locale-switcher-item {
  display: flex;
  align-items: center;
}

:deep(.locale-switcher--header) {
  margin-right: 0;
  font-size: 18px;
  line-height: 1.2;
}

:deep(.locale-switcher--header .locale-switcher__btn) {
  font-size: 18px;
  font-weight: 400;
  padding: 0 2px;
}

:deep(.locale-switcher--header .locale-switcher__btn.is-active) {
  font-weight: 600;
}

:deep(.locale-switcher--mobile) {
  margin-right: 12px;
}
</style>
